import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Helmet } from 'react-helmet-async';
import Loading from '@/components/Loading';
import AppCallToAction from './AppCallToAction';
import { Article } from '@/types/Article';
import { getLocalArticle, hasLocalArticle } from '@/lib/localArticles';
import './article.scss';

type ArticleLanguage = 'ja' | 'en';

interface ArticleLayoutProps {
    defaultLanguage?: ArticleLanguage;
}

interface ArticleTocItem {
    id: string;
    text: string;
    level: 2 | 3;
}

const toArticleIsoDate = (value: string | undefined, fallback: string): string => {
    const date = new Date(value || fallback);
    if (!Number.isNaN(date.getTime())) {
        return date.toISOString();
    }

    return new Date(fallback).toISOString();
};

const stripHtml = (value: string): string =>
    value
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/\s+/g, ' ')
        .trim();

const textToHeadingId = (text: string, index: number): string => {
    const normalized = text
        .toLowerCase()
        .replace(/<[^>]+>/g, '')
        .replace(/&[a-z0-9#]+;/gi, '')
        .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
        .replace(/^-+|-+$/g, '');

    return normalized || `section-${index + 1}`;
};

const normalizeArticleHeadings = (
    html: string,
): { content: string; tocItems: ArticleTocItem[] } => {
    const usedIds = new Set<string>();
    const tocItems: ArticleTocItem[] = [];
    let hasParentHeading = false;

    const content = html.replace(
        /<h([1-6])([^>]*)>([\s\S]*?)<\/h\1>/gi,
        (match, rawLevel: string, rawAttributes: string, innerHtml: string) => {
            const text = stripHtml(innerHtml);
            if (!text) return match;
            const sourceLevel = Number(rawLevel);
            const normalizedLevel: 2 | 3 = sourceLevel >= 3 && hasParentHeading ? 3 : 2;
            if (normalizedLevel === 2) {
                hasParentHeading = true;
            }

            const idMatch = rawAttributes.match(/\sid=(["'])(.*?)\1/i);
            const baseId = idMatch?.[2]?.trim() || textToHeadingId(text, tocItems.length);
            let id = baseId;
            let duplicateIndex = 2;
            while (usedIds.has(id)) {
                id = `${baseId}-${duplicateIndex}`;
                duplicateIndex += 1;
            }
            usedIds.add(id);
            tocItems.push({ id, text, level: normalizedLevel });

            const attributesWithoutId = rawAttributes.replace(/\s+id=(["']).*?\1/i, '').trim();
            const normalizedAttributes = attributesWithoutId
                ? ` id="${id}" ${attributesWithoutId}`
                : ` id="${id}"`;

            return `<h${normalizedLevel}${normalizedAttributes}>${innerHtml}</h${normalizedLevel}>`;
        },
    );

    return { content, tocItems };
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const addClassToAttributes = (rawAttributes: string, className: string): string => {
    const classMatch = rawAttributes.match(/\sclass=(["'])(.*?)\1/i);
    if (!classMatch) {
        return `${rawAttributes} class="${className}"`;
    }
    if (classMatch[2].split(/\s+/).includes(className)) {
        return rawAttributes;
    }
    return rawAttributes.replace(
        /\sclass=(["'])(.*?)\1/i,
        ` class=${classMatch[1]}${classMatch[2]} ${className}${classMatch[1]}`,
    );
};

const classifyArticleLists = (html: string): string =>
    html.replace(/<(ul|ol)([^>]*)>([\s\S]*?)<\/\1>/gi, (match, tag: string, rawAttributes: string, innerHtml: string) => {
        if (/\sclass=(["'])(?:(?!\1).)*article-(?:point|problem|step|neutral)-list/i.test(rawAttributes)) {
            return match;
        }

        const text = stripHtml(innerHtml);
        const className = (() => {
            if (tag.toLowerCase() === 'ol') return 'article-step-list';
            if (
                /(思ったより|後回し|疲弊|不足|分かれていない|分からない|偏り|偏って|増えすぎ|減って|かかっている|課題|リスク|悩|不安|焦り|difficult|problem|too much|dropped|less|weak|avoid|waste)/i.test(
                    text,
                )
            ) {
                return 'article-problem-list';
            }
            if (
                /(確認|判断|ポイント|メリット|分かる|わかる|見える|できる|できます|使える|役立|改善|understand|track|review|helps|useful|improve|can )/i.test(
                    text,
                )
            ) {
                return 'article-point-list';
            }
            return 'article-neutral-list';
        })();

        return `<${tag}${addClassToAttributes(rawAttributes, className)}>${innerHtml}</${tag}>`;
    });

const preferredCtaHeadingPattern =
    /(コトミル|kotomil|見える化|可視化|分析|集計|visuali[sz]e|analy[sz]e|analytics|review|track)/i;

const findPreferredCtaHeading = (tocItems: ArticleTocItem[]): ArticleTocItem | null => {
    if (tocItems.length < 4) return null;

    const firstPreferred = tocItems.find((item, index) => {
        if (index === 0) return false;
        return preferredCtaHeadingPattern.test(`${item.id} ${item.text}`);
    });

    return firstPreferred ?? tocItems[Math.ceil(tocItems.length / 2)] ?? null;
};

const splitContentBeforeHeading = (
    html: string,
    tocItems: ArticleTocItem[],
): { before: string; after: string } | null => {
    if (tocItems.length < 4) return null;

    const targetHeading = findPreferredCtaHeading(tocItems);
    if (!targetHeading) return null;

    const headingPattern = new RegExp(
        `<h[23]\\b[^>]*\\sid=(["'])${escapeRegExp(targetHeading.id)}\\1[^>]*>`,
        'i',
    );
    const match = html.match(headingPattern);
    if (!match || match.index == null || match.index <= 0) return null;

    return {
        before: html.slice(0, match.index).trim(),
        after: html.slice(match.index).trim(),
    };
};

/**
 * 記事ページのレイアウトコンポーネント
 * URLパラメータから記事IDを取得し、ローカル記事データを表示する
 */
const ArticleLayout: React.FC<ArticleLayoutProps> = ({ defaultLanguage = 'ja' }) => {
    // URLパラメータから記事IDを取得
    const { slug } = useParams<{ slug: string }>();
    const location = useLocation();
    const navigate = useNavigate();

    // 状態管理
    const [article, setArticle] = useState<Article | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasJaVersion, setHasJaVersion] = useState(false);
    const [hasEnVersion, setHasEnVersion] = useState(false);
    const articleId = article?.id ?? null;
    const articleLanguage: ArticleLanguage = useMemo(() => {
        if (defaultLanguage === 'en') return 'en';
        if (slug?.startsWith('en_')) return 'en';
        return 'ja';
    }, [defaultLanguage, slug]);
    const brandName = 'Kotomil';
    const isEnglishArticle = articleLanguage === 'en';
    const contentId = useMemo(() => {
        if (!slug) return null;
        if (isEnglishArticle && !slug.startsWith('en_')) {
            return `en_${slug}`;
        }
        return slug;
    }, [slug, isEnglishArticle]);
    const pageSlug = useMemo(() => {
        const sourceSlug = slug ?? articleId ?? null;
        if (!sourceSlug) return null;
        if ((isEnglishArticle || sourceSlug.startsWith('en_')) && sourceSlug.startsWith('en_')) {
            return sourceSlug.replace(/^en_/, '');
        }
        return sourceSlug;
    }, [slug, articleId, isEnglishArticle]);

    // 英語コンテンツ用の旧パス（/articles/en_...）へアクセスされた場合は新しい英語ルートへリダイレクト
    useEffect(() => {
        if (!slug) return;
        if (!isEnglishArticle) return;
        if (!slug.startsWith('en_')) return;
        if (location.pathname.startsWith('/en/articles/')) return;

        const targetSlug = slug.replace(/^en_/, '');
        navigate(`/en/articles/${targetSlug}${location.search}`, { replace: true });
    }, [isEnglishArticle, slug, location.pathname, location.search, navigate]);

    // 記事データの取得
    useEffect(() => {
        const fetchArticle = async () => {
            if (!contentId) return;

            setLoading(true);
            try {
                const localArticle = getLocalArticle(contentId);
                if (localArticle) {
                    setArticle(localArticle);
                } else {
                    setArticle(null);
                    setError('記事が見つかりません。');
                }
            } catch (err) {
                console.error('記事の読み込みエラー:', err);
                setError('記事の読み込みに失敗しました。');
            } finally {
                setLoading(false);
            }
        };

        fetchArticle();
    }, [contentId]);

    // 対応する別言語版が存在するかチェックして hreflang/alternate の出力を制御する
    useEffect(() => {
        // 現在表示中の言語は必ず存在するため、先にステートへ反映
        setHasJaVersion(!isEnglishArticle);
        setHasEnVersion(isEnglishArticle);

        if (!pageSlug) {
            return;
        }

        if (isEnglishArticle) {
            setHasJaVersion(hasLocalArticle(pageSlug));
        } else {
            setHasEnVersion(hasLocalArticle(`en_${pageSlug}`));
        }
    }, [isEnglishArticle, pageSlug]);

    // 画像の後の最初のテキストにドロップキャップクラスを追加する
    useEffect(() => {
        if (!article) return; // 記事が読み込まれていない場合は何もしない

        const addDropCapAfterImage = () => {
            const articleContent = document.querySelector('.article-content');
            if (!articleContent) return;

            // 全ての段落要素を取得
            const paragraphs = articleContent.querySelectorAll('p');

            // 既存のドロップキャップクラスをリセット
            paragraphs.forEach((p) => p.classList.remove('drop-cap-after-image'));

            // 最初の画像を見つける
            const firstImage = articleContent.querySelector('img');

            if (firstImage) {
                // 画像の親要素から次の段落を探す（画像がp要素内にある場合を考慮）
                const searchElement = firstImage.closest('p') || firstImage;
                let nextElement = searchElement.nextElementSibling;

                // 画像の後にある最初のp要素を探す
                while (nextElement) {
                    if (nextElement.tagName === 'P' && nextElement.textContent?.trim()) {
                        // 空でない段落が見つかった場合、ドロップキャップクラスを追加
                        nextElement.classList.add('drop-cap-after-image');
                        break;
                    }
                    nextElement = nextElement.nextElementSibling;
                }
            } else {
                // 画像がない場合は最初の段落にドロップキャップを適用
                const firstParagraph = paragraphs[0];
                if (firstParagraph && firstParagraph.textContent?.trim()) {
                    firstParagraph.classList.add('drop-cap-after-image');
                }
            }
        };

        // コンテンツがレンダリングされた後に実行
        const timer = setTimeout(addDropCapAfterImage, 500); // タイマーを長めに設定

        return () => clearTimeout(timer);
    }, [article]);

    // ローディング状態
    if (loading) {
        return <Loading />;
    }

    // エラー状態
    if (error) {
        return (
            <div className="error-message">
                <p>{error}</p>
                {isPreview && (
                    <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        プレビューモードでアクセスしています。
                    </p>
                )}
            </div>
        );
    }

    // 記事が見つからない場合
    if (!article) {
        return (
            <div className="not-found">
                <p>記事が見つかりません。</p>
                {isPreview && (
                    <p style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
                        プレビューモードでアクセスしています。
                    </p>
                )}
            </div>
        );
    }

    // プレースホルダーを画像タグに置換
    let processedContent = article.content;

    /**
     * ローカル記事の画像データを使用して、記事本文中のプレースホルダーを実際の画像タグに置換
     * プレースホルダー形式: {{image:altテキスト}}
     * 置換後: <img src="/images/articles/{articleId}/{altテキスト}.{拡張子}" ... />
     */
    if (article.images) {
        const images = article.images as any;
        let mediaItems: { name: string; alt: string }[] = [];

        // ローカル記事のimagesフィールドの処理
        // フィールドがJSON文字列として返される場合があるため、パース処理を行う
        let parsedImages;
        if (typeof images === 'string') {
            try {
                parsedImages = JSON.parse(images);
            } catch (error) {
                parsedImages = null;
            }
        } else {
            parsedImages = images;
        }

        // パース済みデータからmediaItems配列を取得
        if (parsedImages?.mediaItems && Array.isArray(parsedImages.mediaItems)) {
            mediaItems = parsedImages.mediaItems;
        }

        // 各画像アイテムに対してプレースホルダーの置換処理を実行
        if (mediaItems.length > 0 && articleId) {
            mediaItems.forEach((mediaItem) => {
                if (mediaItem.alt && mediaItem.name) {
                    const placeholder = `{{image:${mediaItem.alt}}}`;

                    // nameフィールドから直接ファイル名を取得（拡張子込み）
                    const fileName = mediaItem.name;

                    // アプリケーション側で管理している画像ファイルへのパスを生成
                    // 英語記事の画像は /images/articles/en/{contentId}/ 配下に配置
                    const assetPathPrefix = isEnglishArticle ? `en/${articleId}` : articleId;
                    const imageTag = `<img src="/images/articles/${assetPathPrefix}/${fileName}" alt="${mediaItem.alt}" loading="lazy" class="image-protection" style="display: block; margin: 2rem auto; max-width: 100%; height: auto; -webkit-user-drag: none; user-select: none; pointer-events: none;" />`;

                    // プレースホルダーを画像タグに置換（グローバル置換）
                    processedContent = processedContent.replace(
                        new RegExp(placeholder, 'g'),
                        imageTag,
                    );
                }
            });
        }
    }

    const { content: normalizedHeadingContent, tocItems } =
        normalizeArticleHeadings(processedContent);
    const classifiedContent = classifyArticleLists(normalizedHeadingContent);

    // HTMLコンテンツのサニタイズ（XSS対策）
    const sanitizedContent = DOMPurify.sanitize(classifiedContent);
    const shouldShowTableOfContents = tocItems.length >= 2;
    const middleContent = splitContentBeforeHeading(sanitizedContent, tocItems);

    // SEO用のメタ情報を生成
    const metaTitle = `${article.title} | Kotomil`;
    const defaultDescriptions: Record<ArticleLanguage, string> = {
        ja: 'Kotomilの記事をご覧ください。',
        en: 'Read the latest Kotomil articles in English.',
    };
    const metaDescription = article.description || defaultDescriptions[articleLanguage];
    // 本番環境のベースURLを設定（プリレンダリング時にも正しいURLを生成）
    const baseUrl = import.meta.env.VITE_BASE_URL || 'https://kotomil.com';
    const pagePath =
        pageSlug != null
            ? isEnglishArticle
                ? `en/articles/${pageSlug}`
                : `articles/${pageSlug}`
            : isEnglishArticle
              ? 'en/articles'
              : 'articles';
    const pageUrl = `${baseUrl}/${pagePath}`;

    // 記事本文内の最初の画像をOG/Twitterカードのサムネイルに採用し、
    // 該当がない場合は既存の固定画像にフォールバックする
    const defaultOgImage = `${baseUrl}/images/ogp/kotomil_og_image.jpg`;
    const firstImageSrc = (() => {
        try {
            const match = processedContent.match(/<img [^>]*src=["']([^"']+)["'][^>]*>/i);
            return match ? match[1] : null;
        } catch {
            return null;
        }
    })();

    const toAbsoluteUrl = (url: string | null): string | null => {
        if (!url) return null;
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        if (url.startsWith('//')) return `https:${url}`;
        if (url.startsWith('/')) return `${baseUrl}${url}`;
        return `${baseUrl}/${url.replace(/^\.?\//, '')}`;
    };

    const metaImage = toAbsoluteUrl(article.ogImage) || toAbsoluteUrl(firstImageSrc) || defaultOgImage;
    const metaImageAlt =
        article.description && article.description.trim().length > 0
            ? article.description
            : article.title;
    const articlePublishedAt = toArticleIsoDate(article.publishedAt, article.publishedAt);
    const articleModifiedAt = toArticleIsoDate(article.updateAt, article.publishedAt);
    const ogLocale = isEnglishArticle ? 'en_US' : 'ja_JP';
    const alternateOgLocale = isEnglishArticle
        ? hasJaVersion
            ? 'ja_JP'
            : null
        : hasEnVersion
          ? 'en_US'
          : null;
    // 相互参照用のURL
    const jaPagePath = pageSlug != null ? `articles/${pageSlug}` : 'articles';
    const enPagePath = pageSlug != null ? `en/articles/${pageSlug}` : 'en/articles';
    const jaUrl = `${baseUrl}/${jaPagePath}`;
    const enUrl = `${baseUrl}/${enPagePath}`;
    const alternateLinks: { lang: string; href: string }[] = [];
    if (hasEnVersion) {
        alternateLinks.push({ lang: 'en', href: enUrl });
    }
    if (hasJaVersion) {
        alternateLinks.push({ lang: 'ja', href: jaUrl });
    }
    alternateLinks.push({
        lang: 'x-default',
        href: hasEnVersion ? enUrl : jaUrl,
    });

    /**
     * 構造化データの作成（JSON-LD形式）
     * Googleの検索結果にリッチスニペットが表示される可能性が高まる
     */
    const structuredData = {
        // 構造化データの基本情報
        '@context': 'https://schema.org',
        // 記事の種類
        '@type': 'Article',
        // 記事のタイトル
        headline: article.title,
        // 記事の説明
        description: article.description,
        inLanguage: articleLanguage === 'en' ? 'en' : 'ja',
        // 記事のサムネイル
        image: metaImage,
        // 記事の公開日
        datePublished: articlePublishedAt,
        dateModified: articleModifiedAt,
        // 記事の著者
        author: {
            '@type': 'Organization',
            // 著者の名前
            name: 'Kotomil',
            // 著者のURL
            url: baseUrl,
        },
        // 記事の出版社
        publisher: {
            '@type': 'Organization',
            // 出版社の名前
            name: 'Kotomil',
            // 出版社のURL
            url: baseUrl,
            // 出版社のロゴ
            logo: {
                '@type': 'ImageObject',
                // ロゴのURL
                url: `${baseUrl}/images/icon/kotomil_icon_512x512.png`,
                // ロゴの幅
                width: 512,
                // ロゴの高さ
                height: 512,
            },
        },
        // 記事のメインエンティティ
        mainEntityOfPage: {
            // メインエンティティの種類
            '@type': 'WebPage',
            // メインエンティティのID
            '@id': pageUrl,
        },
    };

    // WebSite構造化データ（サイト名のSEO対策）
    const websiteStructuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: brandName,
        url: articleLanguage === 'en' ? `${baseUrl}/en` : `${baseUrl}/ja`,
        potentialAction: {
            '@type': 'SearchAction',
            target: `${baseUrl}/search?q={search_term_string}`,
            'query-input': 'required name=search_term_string',
        },
    };

    return (
        <>
            {/* SEO用のメタデータ */}
            <Helmet htmlAttributes={{ lang: articleLanguage }}>
                {/* タイトル設定 */}
                <title>{metaTitle}</title>

                {/* 基本的なmeta情報 */}
                <meta name="description" content={metaDescription} />
                <meta name="author" content="Kotomil" />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href={pageUrl} />
                {alternateLinks.map(({ lang, href }) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={href} />
                ))}

                {/* Open Graph設定 */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={metaDescription} />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:site_name" content={brandName} />
                <meta property="og:image" content={metaImage} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />
                <meta property="og:image:alt" content={metaImageAlt} />
                <meta property="og:locale" content={ogLocale} />
                {alternateOgLocale && (
                    <meta property="og:locale:alternate" content={alternateOgLocale} />
                )}

                {/* Twitter Card設定 */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:site" content="@KotomilApp" />
                <meta name="twitter:title" content={metaTitle} />
                <meta name="twitter:description" content={metaDescription} />
                <meta name="twitter:image" content={metaImage} />
                <meta name="twitter:image:alt" content={metaImageAlt} />

                {/* 記事固有のメタ情報 */}
                <meta property="article:published_time" content={articlePublishedAt} />
                <meta property="article:modified_time" content={articleModifiedAt} />
                <meta property="article:author" content="Kotomil" />
                <meta property="article:section" content={isEnglishArticle ? 'Article' : '記事'} />

                {/* 構造化データ（WebSite） */}
                <script type="application/ld+json">{JSON.stringify(websiteStructuredData)}</script>
                {/* 構造化データ（Article） */}
                <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
            </Helmet>

            {/* 記事コンテンツ */}
            <main className="article-main">
                <article className="article-layout">
                    {/* 記事タイトル */}
                    <header>
                        <h1 className="article-title">{article.title}</h1>
                    </header>

                    {shouldShowTableOfContents && (
                        <nav
                            className="article-toc"
                            aria-label={isEnglishArticle ? 'Table of contents' : '目次'}
                        >
                            <details className="article-toc-disclosure" open>
                                <summary className="article-toc-title">
                                    {isEnglishArticle ? 'Table of contents' : '目次'}
                                </summary>
                                <ol className="article-toc-list">
                                    {tocItems.map((item) => (
                                        <li
                                            key={item.id}
                                            className={`article-toc-item article-toc-item-level-${item.level}`}
                                        >
                                            <a href={`#${item.id}`}>{item.text}</a>
                                        </li>
                                    ))}
                                </ol>
                            </details>
                        </nav>
                    )}

                    {/* 記事本文 */}
                    {middleContent ? (
                        <>
                            <div
                                className="article-content"
                                dangerouslySetInnerHTML={{ __html: middleContent.before }}
                            />
                            <AppCallToAction variant="inline" />
                            <div
                                className="article-content article-content-after-inline-cta"
                                dangerouslySetInnerHTML={{ __html: middleContent.after }}
                            />
                        </>
                    ) : (
                        <div
                            className="article-content"
                            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                        />
                    )}
                    {/* CTAコンポーネント */}
                    <AppCallToAction />
                </article>
            </main>
        </>
    );
};

export default ArticleLayout;
